import { useState, useEffect, useCallback, useRef } from "react";
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  InlineDrawer,
  Button,
  makeStyles,
  tokens,
  Spinner,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import {
  listConversations,
  loadConversation,
  type Conversation,
} from "../conversation-history";

const useStyles = makeStyles({
  drawer: {
    width: "280px",
    minWidth: "280px",
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    "@media (max-width: 768px)": {
      position: "absolute",
      zIndex: 100,
      height: "100%",
    },
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "4px 0",
  },
  item: {
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    border: "none",
    background: "none",
    textAlign: "left",
    width: "100%",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  itemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  itemTitle: {
    fontSize: "13px",
    fontWeight: tokens.fontWeightSemibold,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemMeta: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    display: "flex",
    justifyContent: "space-between",
    marginTop: "2px",
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    padding: "1rem",
    fontSize: "13px",
    textAlign: "center" as const,
  },
  loadedConv: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    padding: "0.5rem",
    flex: 1,
    overflow: "auto",
  },
  loadedHeader: {
    padding: "0.5rem",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    "& h4": { margin: 0, fontSize: "14px" },
    "& p": {
      margin: "2px 0 0",
      fontSize: "12px",
      color: tokens.colorNeutralForeground3,
    },
  },
  loadedMessage: {
    padding: "6px 10px",
    borderRadius: "8px",
    maxWidth: "85%",
    fontSize: "13px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userMsg: {
    alignSelf: "flex-end",
    backgroundColor: "#0078d4",
    color: "#fff",
  },
  botMsg: {
    alignSelf: "flex-start",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  roleLabel: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: "2px",
    display: "block",
    opacity: 0.7,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    padding: "0.5rem 0",
    "&::before, &::after": {
      content: '""',
      flex: 1,
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
});

interface Props {
  open: boolean;
  onToggle: () => void;
  version: number; // bump to refresh list
}

export function ConversationSidebar({ open, onToggle, version }: Props) {
  const styles = useStyles();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loadedConv, setLoadedConv] = useState<Conversation | null>(null);

  // Load list
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listConversations();
      setConversations(list);
    } catch (err) {
      console.error("Failed to load conversations", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, version, refresh]);

  const handleSelect = useCallback(async (id: string) => {
    setActiveId(id);
    try {
      const conv = await loadConversation(id);
      setLoadedConv(conv);
    } catch (err: any) {
      alert("Failed to load conversation: " + err.message);
    }
  }, []);

  if (!open) return null;

  return (
    <InlineDrawer open={open} className={styles.drawer} position="start">
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onToggle}
              size="small"
            />
          }
        >
          History
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {loadedConv ? (
          <>
            <Button
              appearance="subtle"
              size="small"
              onClick={() => {
                setLoadedConv(null);
                setActiveId(null);
              }}
              style={{ marginBottom: "0.5rem" }}
            >
              ← Back to list
            </Button>
            <div className={styles.loadedHeader}>
              <h4>📜 {loadedConv.title}</h4>
              <p>{loadedConv.messages?.length || 0} messages</p>
            </div>
            <div className={styles.loadedConv}>
              {(loadedConv.messages || []).map((msg, i) => (
                <div
                  key={i}
                  className={`${styles.loadedMessage} ${msg.role === "user" ? styles.userMsg : styles.botMsg}`}
                >
                  <span className={styles.roleLabel}>
                    {msg.role === "user" ? "You" : "Assistant"}
                  </span>
                  {msg.text}
                </div>
              ))}
            </div>
          </>
        ) : loading ? (
          <Spinner size="small" label="Loading..." />
        ) : conversations.length === 0 ? (
          <p className={styles.empty}>No saved conversations yet</p>
        ) : (
          <div className={styles.list}>
            {conversations.map((conv) => {
              const d = new Date(conv.timestamp);
              return (
                <button
                  key={conv.conversationId}
                  className={`${styles.item} ${conv.conversationId === activeId ? styles.itemActive : ""}`}
                  onClick={() => handleSelect(conv.conversationId)}
                >
                  <div className={styles.itemTitle}>{conv.title}</div>
                  <div className={styles.itemMeta}>
                    <span>{conv.messageCount} msgs</span>
                    <span>{d.toLocaleDateString()}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DrawerBody>
    </InlineDrawer>
  );
}
